/**
 * Sprint V101.f (So 20.09.2026, Andreas) — Provider-adaptiver DOI-Hinweis.
 *
 * Aus einer eingegebenen E-Mail leitet diese Utility den Provider-Cluster
 * ab (Gmail, Outlook/Microsoft, GMX, Web.de, Yahoo, Apple, T-Online,
 * Posteo/Mailbox oder Generic). Das erhöht die Relevanz der Post-Submit-
 * Kachel („Prüfe Spam/Werbung"), weil der User genau weiß, WO er suchen
 * soll — und wir vermeiden generisches „schau mal im Spam"-Rauschen.
 *
 * Wichtig — Datensparsamkeit:
 *   - KEIN Tracking, keine Netzwerk-Requests
 *   - Nur clientseitige String-Zerlegung
 *   - Keine PII wird protokolliert
 */

/** Provider-Definitionen. Reihenfolge egal, Match ist exact per Domain. */
const PROVIDERS = {
  gmail: {
    label: "Gmail",
    // Deep-Search nach unserem Absender. Öffnet Webmail bei angemeldetem User.
    deeplink: "https://mail.google.com/mail/u/0/#search/from%3Aeffektiv-kommunizieren.de",
    deeplinkLabel: "Gmail im Browser öffnen",
    locations: [
      'Tab „Werbung" oder „Promotions"',
      'Ordner „Spam"',
    ],
    tip: "Ziehe die Mail per Drag & Drop in dein Hauptpostfach — dann kommen alle künftigen Mails direkt an.",
  },
  outlook: {
    label: "Outlook / Live / Hotmail",
    deeplink: "https://outlook.live.com/mail/0/",
    deeplinkLabel: "Outlook im Browser öffnen",
    locations: [
      'Ordner „Andere" (bei aktiver Focused Inbox)',
      'Ordner „Junk-E-Mail"',
    ],
    tip: 'Klick oben auf „Kein Junk" und füg den Absender zu deinen Kontakten hinzu.',
  },
  gmx: {
    label: "GMX",
    deeplink: "https://email.gmx.net",
    deeplinkLabel: "GMX im Browser öffnen",
    locations: [
      "Hauptpostfach — meist zuverlässig",
      'Falls nicht: Ordner „Spam"',
    ],
    tip: "GMX stellt fast alles regulär zu.",
  },
  web: {
    label: "Web.de",
    deeplink: "https://web.de",
    deeplinkLabel: "Web.de im Browser öffnen",
    locations: [
      "Hauptpostfach",
      'Ordner „Spamverdacht"',
    ],
    tip: "Web.de sortiert selten falsch — schau kurz in beiden Ordnern.",
  },
  yahoo: {
    label: "Yahoo",
    deeplink: "https://mail.yahoo.com",
    deeplinkLabel: "Yahoo im Browser öffnen",
    locations: [
      'Ordner „Massen-E-Mail" oder „Bulk"',
      'Ordner „Spam"',
    ],
    tip: 'Markier die Mail als „Kein Spam", dann werden künftige Mails zugestellt.',
  },
  apple: {
    label: "iCloud / Apple Mail",
    deeplink: "https://www.icloud.com/mail",
    deeplinkLabel: "iCloud im Browser öffnen",
    locations: [
      'Ordner „Junk-E-Mail"',
    ],
    tip: 'Füg „info@effektiv-kommunizieren.de" zu deinen Kontakten hinzu — dann landet nichts mehr im Junk.',
  },
  tonline: {
    label: "T-Online",
    deeplink: "https://email.t-online.de",
    deeplinkLabel: "T-Online im Browser öffnen",
    locations: [
      "Hauptpostfach",
      'Ordner „Spam" oder „Werbung"',
    ],
    tip: "Meist zuverlässig — falls doch weg, im Spam-Ordner suchen.",
  },
  posteo: {
    label: "Posteo / Mailbox.org",
    deeplink: null,
    deeplinkLabel: null,
    locations: [
      'Ordner „Spam"',
    ],
    tip: 'Datenschutz-Provider filtern streng. Falls die Mail dort landet, „Kein Spam" markieren.',
  },
  generic: {
    label: null,
    deeplink: null,
    deeplinkLabel: null,
    locations: [
      'Ordner „Spam"',
      'Ordner „Werbung" oder „Promotions"',
      'Ordner „Junk"',
    ],
    tip: 'Falls die Mail bei dir landet: markier sie als „Kein Spam", damit künftige Mails direkt ankommen.',
  },
};

// Domain-Mapping: welche eingegebene Domain zählt zu welchem Provider-Cluster?
const DOMAIN_MAP = {
  // Gmail (inkl. googlemail.com — alter deutscher Alias)
  "gmail.com": "gmail",
  "googlemail.com": "gmail",
  // Microsoft
  "outlook.com": "outlook",
  "outlook.de": "outlook",
  "outlook.at": "outlook",
  "hotmail.com": "outlook",
  "hotmail.de": "outlook",
  "hotmail.at": "outlook",
  "live.com": "outlook",
  "live.de": "outlook",
  "live.at": "outlook",
  "msn.com": "outlook",
  // GMX (DE/AT/CH)
  "gmx.de": "gmx",
  "gmx.net": "gmx",
  "gmx.at": "gmx",
  "gmx.ch": "gmx",
  "gmx.com": "gmx",
  // Web.de + United Internet
  "web.de": "web",
  // Yahoo
  "yahoo.com": "yahoo",
  "yahoo.de": "yahoo",
  "yahoo.co.uk": "yahoo",
  "ymail.com": "yahoo",
  // Apple
  "icloud.com": "apple",
  "me.com": "apple",
  "mac.com": "apple",
  // T-Online
  "t-online.de": "tonline",
  // Posteo / Mailbox.org
  "posteo.de": "posteo",
  "posteo.net": "posteo",
  "mailbox.org": "posteo",
};

/**
 * Aus einer E-Mail den Provider-Hint ableiten.
 * @param {string} email — irgendein String, wird lax validiert
 * @returns {{ providerKey: string, label: string|null, deeplink: string|null,
 *             deeplinkLabel: string|null, locations: string[], tip: string }}
 */
export function getEmailProviderHint(email) {
  const domain = extractDomain(email);
  const key = domain ? (DOMAIN_MAP[domain] || "generic") : "generic";
  return { providerKey: key, ...PROVIDERS[key] };
}

/** Nur die Domain extrahieren, komplett lowercase, ohne Sub-Domain-Beibehaltung. */
function extractDomain(email) {
  if (!email || typeof email !== "string") return "";
  const at = email.lastIndexOf("@");
  if (at < 0 || at === email.length - 1) return "";
  return email.slice(at + 1).trim().toLowerCase();
}
